<script setup lang="ts">
    import { reactive, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { type AccountUser } from './AccountUsers.access';

    const props = defineProps<{
        show: boolean;
        title: string;
        fromUsers: AccountUser[];
        allUsers: AccountUser[];
        changeOwner: (fromUserId: string, toUserId: string) => Promise<void>;
    }>();

    const emit = defineEmits<{
        close: [];
        changed: [];
    }>();

    const state = reactive({
        fromUserId: '',
        toUserId: '',
        isBusy: false,
    });

    watch(
        () => props.show,
        (show) => {
            if (!show) return;
            state.fromUserId = '';
            state.toUserId = '';
        }
    );

    function close(): void {
        emit('close');
    }

    async function onChangeOwner(): Promise<void> {
        if (!state.fromUserId || !state.toUserId || state.isBusy) return;

        state.isBusy = true;

        try {
            await props.changeOwner(state.fromUserId, state.toUserId);
            emit('changed');
            close();
            bsAlert({ type: AlertTypes.Success });
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isBusy = false;
        }
    }
</script>

<template>
    <Modal size="sm" :show="props.show" :title="props.title" @close="close">
        <template #content>
            <FormSelect
                class="form-group"
                :label="$Resources.CurrentOwnerHeadingLabel"
                :emptyOption="true"
                :modelValue="state.fromUserId"
                :options="props.fromUsers.map((user) => ({ value: user.UserId, label: user.Email }))"
                @update:modelValue="state.fromUserId = $event" />

            <FormSelect
                class="form-group"
                :label="$Resources.NewOwnerHeadingLabel"
                :emptyOption="true"
                :modelValue="state.toUserId"
                :options="props.allUsers.filter((u) => u.UserId !== state.fromUserId).map((user) => ({ value: user.UserId, label: user.Email }))"
                @update:modelValue="state.toUserId = $event" />
        </template>

        <template #button>
            <button
                type="button"
                class="btn btn-primary"
                :class="{ 'is-busy': state.isBusy }"
                :disabled="!state.fromUserId || !state.toUserId || state.isBusy"
                @click="onChangeOwner">
                {{ $Resources.ChangeOwnerButton }}
            </button>
        </template>
    </Modal>
</template>

